var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// x-dropdown-selection.ts
import { A } from "@adornis/base/env-info.js";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { css } from "@adornis/chemistry/directives/css.js";
import { injectAttributesOf } from "@adornis/chemistry/directives/inject-attributes.js";
import "@adornis/chemistry/elements/components/x-flat-loader.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-formfield-wrapper.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import { inputToken } from "@adornis/chemistry/elements/theming/input-token.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import "@adornis/chemistry/renderable.js";
import { FormField } from "@adornis/formfield/form-field.js";
import "@adornis/forms/x-input.js";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { guard } from "lit/directives/guard.js";
import { ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { combineLatest, fromEvent, timer } from "rxjs";
import { debounce, delay, switchMap, takeUntil, tap } from "rxjs/operators";
import "./x-dropdown.js";
import { findParentOf, getActiveElement } from "./x-dropdown.js";
var XDropdownSelection = class extends FormField {
  constructor() {
    super(...arguments);
    this.selectables = new RXController(this, []);
    this.guardKey = "";
    this.placeholder = "";
    this.emptyState = "No selectables found!";
    this["use-slot"] = false;
    this.debounceInput = 0;
    this._searchString = new RXController(this, "");
    this._searchResultSelectables = new RXController(
      this,
      combineLatest([
        this._searchString.observable.pipe(
          debounce(() => {
            this._loading = true;
            return timer(this.debounceInput);
          })
        ),
        this.selectables.observable
      ]).pipe(
        switchMap(
          async ([str, selectables]) => (!str && selectables?.length || !this._activated ? selectables : this.search?.(str, this)) ?? []
        ),
        tap((arr) => {
          this._loading = false;
          if (!Array.isArray(arr))
            throw new Error("search callback didnt give me an array");
        })
      )
    );
    this._loading = false;
    this._activated = false;
    this._preSelectedItem = -1;
    this._width = 0;
    this.renderItem = (item, index, value) => html`
    <x-flex
      ${css({
      padding: `${this.spacing.sm} ${this.spacing.md}`,
      cursor: "pointer"
    })}
    >
      <x-text> ${this.renderString(item, index)} </x-text>
    </x-flex>
  `;
    this.renderString = (x) => typeof x === "string" || typeof x === "number" ? x + "" : x === null || x === void 0 ? "" : JSON.stringify(x);
    this.renderSelected = (x) => this.renderString(x);
    this.resolveID = (x) => x?._id || A.getGloballyUniqueID();
    this.search = null;
    this._ignoreNextBlur = false;
    this._ignoreNextFocus = false;
    this._ignoreBlur = false;
    /**
     * Set this to false to disable default trigger logic.
     * This enables you to handle opening and closing the dropdown with your own code without overwriting _triggerWrapper.
     *
     * This also disables the default event handler for _onBlur and _onFocus, so make sure your trigger calls these on user interaction!
     */
    this._ignoreDefaultTrigger = false;
    /**
     * If the dropdown toggles the item list if clicked (true, default) or just opens it (false).
     * Setting this to false enables double clicks on the trigger.
     */
    this._toggleOnClick = true;
  }
  get inputToken() {
    return inputToken(this);
  }
  styles() {
    return [
      {
        ":host([error]) #trigger": { borderColor: this.colors.tone.error + " !important" }
      }
    ];
  }
  connectedCallback() {
    super.connectedCallback();
    if (!this._ignoreDefaultTrigger) {
      this.setAttribute("tabindex", "0");
      fromEvent(this, "focus", { passive: true }).pipe(delay(0), takeUntil(this.disconnected)).subscribe((e) => this._onFocus(e));
      fromEvent(this, "blur", { passive: true }).pipe(delay(0), takeUntil(this.disconnected)).subscribe((e) => this._onBlur(e));
    }
    fromEvent(this, "keydown").pipe(takeUntil(this.disconnected)).subscribe((e) => this._onKeydown(e));
  }
  get hasValue() {
    const val = this.value.value;
    return val !== null && val !== void 0 && val !== "" && !!this.renderSelected(val);
  }
  get showLabel() {
    return this.placeholder && this.hasValue || this.error;
  }
  async getUpdateComplete() {
    const result = await super.getUpdateComplete();
    await new Promise((resolve) => requestAnimationFrame(() => resolve()));
    return result;
  }
  render() {
    if (this.selectables.value?.length && this.search) {
      console.error(
        "x-dropdown-selections wont render selectables if a search function is given. Please provide either selectables or a search function, but not both."
      );
      return xComponents(html` <x-text tone="error"> invalid properties for x-dropdown-selection </x-text> `);
    }
    if (!this._activated)
      return xComponents(html` <x-flex> ${this._triggerWrapper()} </x-flex> `);
    return xComponents(html`
      <x-flex>
        <x-dropdown id="dropdown" ${ref((dropdown) => this._adropdown = dropdown)}>
          ${this._triggerWrapper()} ${this._dropdownContentWrapper()}
        </x-dropdown>
      </x-flex>
    `);
  }
  _triggerWrapper() {
    return html`
      <div
        slot="trigger"
        @click=${async () => {
      if (this.disabled)
        return;
      this._width = this.offsetWidth;
      if (this._ignoreDefaultTrigger)
        return;
      if (!this._activated) {
        this._activated = true;
        await this._activate();
        await this.updateComplete;
      }
      if (this._toggleOnClick) {
        this._adropdown?.toggle();
      } else {
        this._adropdown?.open();
      }
    }}
        ${css({ cursor: !this.disabled ? "pointer" : "initial" })}
      >
        ${this._trigger()}
      </div>
    `;
  }
  _trigger() {
    if (this.placeholder || !this["use-slot"]) {
      const renderSelected = this.hasValue ? this.renderSelected(this.value.value) : this.placeholder;
      return html`
        <x-formfield-wrapper
          ${injectAttributesOf(this, ["tabindex"])}
          icon=${this._icon()}
          ?show-label=${this.showLabel}
          @cleared=${() => this._clear()}
          @icon-click=${() => this.dispatchEvent(new CustomEvent("icon-click"))}
        >
          ${!this.hasValue || typeof renderSelected === "string" ? html`
                <x-text
                  flex
                  tone=${this.hasValue ? "" : "placeholder"}
                  ellipsis
                  value=${this.hasValue ? this.renderSelected(this.value.value) : this.placeholder}
                >
                </x-text>
              ` : renderSelected}
        </x-formfield-wrapper>
      `;
    }
    return html` <slot></slot> `;
  }
  _icon() {
    return "keyboard_arrow_down";
  }
  _dropdownContentWrapper() {
    return html`
      <x-dropdown-selection-content
        slot="dropdown-content"
        ${ref((dropdownContent) => this._dropdownContentElement = dropdownContent)}
        ${css({ width: this._width ? this._width + "px" : "unset" })}
      >
        ${guard(this._guardTrigger(), () => xComponents(this._dropdownContent()))}
      </x-dropdown-selection-content>
    `;
  }
  _dropdownContent() {
    return html` ${this._search()} ${this._items()} `;
  }
  _guardTrigger() {
    return [
      this._searchResultSelectables.value?.map((s) => this.guardKey ? s[this.guardKey] : s).join(),
      this._preSelectedItem,
      this._loading
    ];
  }
  _search() {
    return this.search ? html`
          <x-input
            ${css({ paddingTop: this.spacing.sm })}
            placeholder="Suchen..."
            placeholder-mode="hide"
            @value-changed=${(e) => this._searchString.next(e.detail.value)}
            disable-blur-submit
            @mousedown=${() => this._ignoreNextBlur = true}
          ></x-input>
        ` : nothing;
  }
  _items() {
    if (this._loading)
      return this._loader();
    const selectables = this._searchResultSelectables.value;
    if (!selectables?.length)
      return this._empty();
    return html`
      ${repeat(
      selectables,
      (s) => this.resolveID(s),
      (selectable, index) => xComponents(html`
            <div
              ?pre-selected=${this._preSelectedItem === index}
              ?active=${selectable === this.value.value}
              @mousedown=${(e) => {
        this._ignoreBlur = true;
      }}
              @touchstart=${(e) => {
        this._ignoreNextBlur = true;
      }}
              @mouseup=${() => {
        this._ignoreBlur = false;
      }}
              @click=${() => {
        this._choose(selectable);
        this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: this.value.value } }));
      }}
            >
              ${this.renderItem(selectable, index, this.value.value)}
            </div>
          `)
    )}
    `;
  }
  _loader() {
    return html` <x-flex center padding="sm"> <x-flat-loader></x-flat-loader> </x-flex> `;
  }
  _empty() {
    return html`
      <x-flex ${css({ padding: `${this.spacing.sm} ${this.spacing.md}` })}>
        <x-text> ${this.emptyState} </x-text>
      </x-flex>
    `;
  }
  _clear() {
    this._ignoreNextFocus = true;
    this._preSelectedItem = -1;
    this.value.next(null);
    this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: null } }));
    this.dispatchEvent(new CustomEvent("cleared"));
  }
  _choose(selectable) {
    this._adropdown?.close();
    this._preSelectedItem = -1;
    this.value.next(selectable);
  }
  _activate() {
  }
  async _onFocus(event) {
    if (this.disabled)
      return false;
    this._width = this.offsetWidth;
    await new Promise((r) => setTimeout(r, 100));
    if (this._ignoreNextFocus) {
      this._ignoreNextFocus = false;
      return false;
    }
    if (!this._activated) {
      this._activated = true;
      await this._activate();
      await this.updateComplete;
    }
    const element = getActiveElement();
    if (findParentOf(element, (e) => e === this))
      this._adropdown?.open();
    return true;
  }
  _onBlur(event) {
    if (this._ignoreBlur)
      return false;
    if (this._ignoreNextBlur) {
      this._ignoreNextBlur = false;
      return false;
    }
    if (!this._adropdown?.wouldEventCloseMe(event))
      return false;
    this._adropdown?.close();
    return true;
  }
  _onKeydown(e) {
    const selectables = this._searchResultSelectables.value ?? [];
    if (!this._preSelectedItem && this.value.value)
      this._preSelectedItem = 0;
    if (this._preSelectedItem === -1 && this.value.value)
      this._preSelectedItem = selectables.indexOf(this.value.value);
    switch (e.key) {
      case "ArrowUp":
      case "Up": {
        e.stopPropagation();
        e.preventDefault();
        this._preSelectedItem = this._preSelectedItem === -1 || this._preSelectedItem - 1 < 0 ? selectables.length - 1 : this._preSelectedItem - 1;
        this._scrollToPreselect();
        break;
      }
      case "ArrowDown":
      case "Down": {
        e.stopPropagation();
        e.preventDefault();
        this._preSelectedItem = this._preSelectedItem === -1 || this._preSelectedItem + 1 > selectables.length - 1 ? 0 : this._preSelectedItem + 1;
        this._scrollToPreselect();
        break;
      }
      case "Enter": {
        e.stopPropagation();
        const selectable = selectables[this._preSelectedItem] ?? // if only one item in selectables choose it when pressing enter no matter what is currently selected
        (selectables.length === 1 && selectables[0] ? selectables[0] : void 0);
        if (selectable !== void 0) {
          this._choose(selectable);
          this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: this.value.value } }));
          this._adropdown?.close();
          break;
        }
        if (!selectables.length || this._loading)
          return;
        break;
      }
      case "Escape": {
        e.stopPropagation();
        this._adropdown?.close();
        break;
      }
      default:
        break;
    }
  }
  _scrollToPreselect() {
    const target = this._dropdownContentElement?.querySelector(`:nth-child(${this._preSelectedItem + 1})`);
    target?.scrollIntoView();
  }
};
__decorateClass([
  property()
], XDropdownSelection.prototype, "selectables", 2);
__decorateClass([
  property()
], XDropdownSelection.prototype, "guardKey", 2);
__decorateClass([
  property()
], XDropdownSelection.prototype, "placeholder", 2);
__decorateClass([
  property()
], XDropdownSelection.prototype, "emptyState", 2);
__decorateClass([
  property({ type: Boolean })
], XDropdownSelection.prototype, "use-slot", 2);
__decorateClass([
  property({ type: Number, attribute: "debounce-input" })
], XDropdownSelection.prototype, "debounceInput", 2);
__decorateClass([
  state()
], XDropdownSelection.prototype, "_searchString", 2);
__decorateClass([
  state()
], XDropdownSelection.prototype, "_searchResultSelectables", 2);
__decorateClass([
  state()
], XDropdownSelection.prototype, "_loading", 2);
__decorateClass([
  state()
], XDropdownSelection.prototype, "_activated", 2);
__decorateClass([
  state()
], XDropdownSelection.prototype, "_preSelectedItem", 2);
__decorateClass([
  state()
], XDropdownSelection.prototype, "_width", 2);
XDropdownSelection = __decorateClass([
  customElement("x-dropdown-selection")
], XDropdownSelection);
var XDropdownSelectionContent = class extends ChemistryLitElement {
  styles() {
    return [
      {
        ":host": {
          display: "flex",
          flexDirection: "column",
          background: `var(--color-base-100, ${this.colors.white})`,
          borderRadius: this.sizes.borderRadius,
          boxShadow: `0 1px 3px 0 var(--color-base-300, #d4d4d5), 0 0 0 1px var(--color-base-300, #d4d4d5)`,
          userSelect: "none",
          maxHeight: "300px",
          overflowY: "auto",
          [`::slotted(*)`]: {
            borderBottom: `1px solid var(--color-base-300, ${this.colors.neutral.shade(100)})`
          },
          [`::slotted(*:hover)`]: { background: `var(--color-base-200, ${this.colors.neutral.shade(50)})` },
          [`::slotted([pre-selected])`]: { background: `var(--color-base-200, ${this.colors.neutral.shade(50)})` },
          [`::slotted([active])`]: { background: `var(--color-base-300, ${this.colors.neutral.shade(100)})` }
        }
      }
    ];
  }
  render() {
    return html` <slot></slot> `;
  }
};
XDropdownSelectionContent = __decorateClass([
  customElement("x-dropdown-selection-content")
], XDropdownSelectionContent);
export {
  XDropdownSelection,
  XDropdownSelectionContent
};
